import { KeyboardEvent, ReactElement, useCallback, useMemo, useState } from 'react';
import { TableComponents, TableVirtuoso } from 'react-virtuoso';
import TableCore, { buildColGroup } from './TableCore';
import { BodyRow, StyledTable } from './styles';
import { ScrollingServerTableProps, TableResponse } from './types';
import useServerTable from './useServerTable';

const ScrollingServerTable = <Row,>(props: ScrollingServerTableProps<Row>): ReactElement => {
  const {
    columns,
    getRowKey,
    getRowStyle,
    onRowClick,
    selectedRowKey,
  } = props;
  const [rows, setRows] = useState<Row[]>([]);
  const [nextPage, setNextPage] = useState(0);

  const resetRows = useCallback(() => {
    setRows([]);
    setNextPage(0);
  }, []);
  const handleResponse = useCallback((response: TableResponse<Row>, requestedPage: number) => {
    setRows((currentRows) => (
      requestedPage === 0 ? response.items : [...currentRows, ...response.items]
    ));
    setNextPage(requestedPage + 1);
  }, []);
  const table = useServerTable({
    ...props,
    onReset: resetRows,
    onResponse: handleResponse,
  });

  const loadNextPage = useCallback(() => {
    if (table.loadingRef.current || table.error) return;
    table.loadPage(nextPage, false);
  }, [nextPage, table.error, table.loadPage, table.loadingRef]);

  const rowComponents = useMemo<TableComponents<Row>>(() => ({
    Table: (tableProps) => (
      <StyledTable {...tableProps}>
        {buildColGroup(columns)}
        {tableProps.children}
      </StyledTable>
    ),
    TableRow: ({ item, ...rowProps }) => {
      const isSelected = selectedRowKey !== undefined && getRowKey(item) === selectedRowKey;
      const handleKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
        if (onRowClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onRowClick(item);
        }
      };
      return (
        <BodyRow
          {...rowProps}
          clickable={Boolean(onRowClick)}
          selected={isSelected}
          style={getRowStyle?.(item)}
          aria-selected={isSelected || undefined}
          onClick={() => onRowClick?.(item)}
          onKeyDown={handleKeyDown}
          tabIndex={onRowClick ? 0 : undefined}
        />
      );
    },
  }), [columns, getRowKey, getRowStyle, onRowClick, selectedRowKey]);

  const renderBody = useCallback((renderRow: (index: number, row: Row) => ReactElement) => (
    <TableVirtuoso<Row>
      style={{ height: '100%' }}
      data={rows}
      components={rowComponents}
      itemContent={renderRow}
      computeItemKey={(_index, row) => getRowKey(row)}
      endReached={loadNextPage}
      increaseViewportBy={{ top: 100, bottom: 300 }}
    />
  ), [getRowKey, loadNextPage, rowComponents, rows]);

  return (
    <TableCore
      {...props}
      {...table}
      rowsLength={rows.length}
      renderBody={renderBody}
    />
  );
};

export default ScrollingServerTable;
